import { useState, useEffect, useMemo } from "react";
import ProductLayout from "../../components/ProductLayout";
import { NewsHero } from "../../components/NewsHero";
import { NewsFilters } from "../../components/NewsFilters";
import { NewsCard } from "../../components/NewsCard";
import { NewsDialog } from "../../components/NewsDialog";
import { NewsPagination } from "../../components/NewsPagination";
import { EmptyState } from "../../components/EmptyState";
import { CATEGORIES, ITEMS_PER_PAGE } from "../../constants/news";
import { NewsItem } from "../../types/news";
import NewsService from "@/services/news/APIs/NewsService";
import { mapNewsToComponentFormat } from "../../utils/newsMapper";
import Loader from "@/common/components/ui/loader";

export default function NewsPage() {
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [currentNewsItems, setCurrentNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch news data for current page only
  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await NewsService.getCapitalMarketNews(
          currentPage,
          ITEMS_PER_PAGE
        );
        if (response?.results) {
          const mappedNews = response.results.map(mapNewsToComponentFormat);
          setCurrentNewsItems(mappedNews);

          // Calculate total pages from API response
          const calculatedTotalPages = Math.ceil(
            response.count / ITEMS_PER_PAGE
          );
          setTotalPages(calculatedTotalPages);
        } else {
          setCurrentNewsItems([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching news:", error);
        setCurrentNewsItems([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [currentPage]);

  // Filter news based on category and search term (client-side filtering on current page)
  const filteredNews = useMemo(() => {
    return currentNewsItems.filter(item => {
      const matchesCategory =
        activeCategory === "all" ||
        item.category.toLowerCase() === activeCategory.toLowerCase();
      const matchesSearch =
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.summary.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [currentNewsItems, activeCategory, searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [activeCategory, searchTerm]);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReadMore = (item: NewsItem) => {
    setSelectedNews(item);
  };

  if (loading) {
    return (
      <ProductLayout>
        <NewsHero />
        <div className="container mx-auto px-4 py-10 md:px-6">
          <div className="flex h-64 items-center justify-center">
            <Loader />
          </div>
        </div>
      </ProductLayout>
    );
  }

  return (
    <ProductLayout>
      <NewsHero />

      <div className="container mx-auto px-4 py-10 md:px-6">
        <NewsFilters
          categories={CATEGORIES}
          activeCategory={activeCategory}
          searchTerm={searchTerm}
          onCategoryChange={handleCategoryChange}
          onSearchChange={handleSearchChange}
        />

        {filteredNews.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredNews.map(item => (
                <NewsCard
                  key={item.id}
                  item={item}
                  onReadMore={handleReadMore}
                />
              ))}
            </div>

            <NewsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : !loading ? (
          <EmptyState />
        ) : null}

        <NewsDialog
          newsItem={selectedNews}
          onClose={() => setSelectedNews(null)}
        />
      </div>
    </ProductLayout>
  );
}
